import { useState } from "react";
import { PaperPlaneTilt, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitRating } from "@/lib/api";

const initialRatingForm = { customer_name: "", fleet_type: "Fleet Operator", score: 5, comment: "" };

const RatingTextField = ({ label, testPrefix, value, onChange, required = false, minLength }) => (
  <label data-testid={`${testPrefix}-label`} className="block text-sm font-bold text-[#09090B]">
    {label}
    <input
      data-testid={`${testPrefix}-input`}
      required={required}
      minLength={minLength}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 h-11 w-full rounded-md border border-zinc-300 px-3 outline-none focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
    />
  </label>
);

const RatingScoreButtons = ({ scoreValue, onChange }) => (
  <div data-testid="rating-score-field" className="block text-sm font-bold text-[#09090B] sm:col-span-2">
    <p data-testid="rating-score-label">Score</p>
    <div data-testid="rating-score-button-group" className="mt-2 grid grid-cols-5 gap-2" role="radiogroup" aria-label="Rating score">
      {[1, 2, 3, 4, 5].map((score) => {
        const isActive = Number(scoreValue) === score;
        return (
          <button
            data-testid={`rating-score-button-${score}`}
            key={score}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(score)}
            className={`h-11 rounded-md border text-sm font-black transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${
              isActive ? "border-[#FF5500] bg-[#FF5500] text-white" : "border-zinc-300 bg-white text-[#52525B] hover:border-[#0055FF] hover:text-[#0055FF]"
            }`}
          >
            {score}★
          </button>
        );
      })}
    </div>
  </div>
);

const RatingCommentField = ({ value, onChange }) => (
  <label data-testid="rating-comment-label" className="block text-sm font-bold text-[#09090B] sm:col-span-2">
    Review
    <textarea
      data-testid="rating-comment-textarea"
      required
      minLength={5}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={4}
      className="mt-2 w-full resize-none rounded-md border border-zinc-300 px-3 py-3 outline-none focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
    />
  </label>
);

export const RatingForm = ({ facility, onRated }) => {
  const [form, setForm] = useState(initialRatingForm);
  const [submitting, setSubmitting] = useState(false);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const updated = await submitRating(facility.id, { ...form, score: Number(form.score) });
      onRated(updated);
      setForm(initialRatingForm);
      toast.success("Rating published", { description: "The facility score has been updated." });
    } catch (error) {
      toast.error("Rating could not be submitted", { description: error.response?.data?.detail || "Please check the form and try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="rating-form" id="ratings" onSubmit={handleSubmit} className="border border-zinc-200 bg-white p-5">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p data-testid="rating-form-eyebrow" className="text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">Customer rating</p>
          <h2 data-testid="rating-form-title" className="font-display text-2xl font-black tracking-normal text-[#09090B]">Rate this facility</h2>
        </div>
        <Star data-testid="rating-form-star-icon" size={28} weight="fill" className="text-[#FF5500]" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <RatingTextField label="Name" testPrefix="rating-name" required minLength={2} value={form.customer_name} onChange={(value) => updateForm("customer_name", value)} />
        <RatingTextField label="Fleet type" testPrefix="rating-fleet" value={form.fleet_type} onChange={(value) => updateForm("fleet_type", value)} />
        <RatingScoreButtons scoreValue={form.score} onChange={(value) => updateForm("score", value)} />
        <RatingCommentField value={form.comment} onChange={(value) => updateForm("comment", value)} />
      </div>

      <button
        data-testid="submit-rating-button"
        type="submit"
        disabled={submitting}
        className="mt-4 inline-flex h-11 items-center gap-2 rounded-md bg-[#0055FF] px-4 text-sm font-black text-white transition-colors duration-200 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:bg-zinc-400"
      >
        <PaperPlaneTilt data-testid="submit-rating-icon" size={18} weight="bold" />
        {submitting ? "Publishing..." : "Publish rating"}
      </button>
    </form>
  );
};